/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, observable } from 'mobx';
import { LuisId } from 'src/luis/types/Resource';
import { SublistStore } from 'src/luis/types/stores/entities/SublistStore';

/**
 * A sublist store factory function.
 *
 * @param id The id of the sublist to create.
 * @param sublist The sublist data.
 * @returns A new instance of a sublist store.
 */
export const createSublistStore = (id: LuisId, sublist: Partial<SublistStore>): SublistStore => {
    const sublistStore: SublistStore = {
        id,
        name: sublist.name,
        values: sublist.values !== undefined ? sublist.values : [],

        /**
         * Renames the entity with the given name.
         *
         * @param name The name to rename the entity with.
         */
        rename(name) {
            this.name = name;
        },

        /**
         * Sets (replaces) the given values to the sublist values.
         *
         * @param values The values to set/replace.
         */
        setValues(values) {
            this.values = values;
        },

        /**
         * Adds the given values to the sublist values.
         *
         * @param values The values to add to the sublist.
         */
        addValues(values) {
            return this.values.push(...values);
        },

        /**
         * Removes the values at the  given indices from
         * the existing sublist values.
         *
         * @param valuesToRemove An array of string values to remove.
         */
        removeValuesByIndex(indices) {
            this.values = this.values.filter((_, index) => !indices.some(i => i === index));
        },

        /**
         * Removes the given values from the existing
         * sublist values.
         *
         * @param valuesToRemove An array of string values to remove.
         */
        removeValuesByWords(valuesToRemove) {
            this.values = this.values.filter(value => valuesToRemove.every(v => v !== value));
        }
    };

    return observable(
        sublistStore,
        { name: observable, values: observable, rename: action, setValues: action, addValues: action, removeValuesByIndex: action },
        { deep: false }
    );
};
